import React, { useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import { secondsToHumanReadable } from '../Utils/timeUtils';
import './TestGraph.css';

// Register ChartJS components and plugins
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  zoomPlugin
);

const TestGraph = ({ 
  selectedTest, 
  chartType, 
  showStats, 
  statsHighlight, 
  stats, 
  annotations, 
  pointRadiusArray 
}) => {
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current) {
        chartRef.current.update();
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const createGradient = (ctx, area, colorStart, colorEnd) => {
    const gradient = ctx.createLinearGradient(0, area.bottom, 0, area.top);
    gradient.addColorStop(0, colorStart);
    gradient.addColorStop(1, colorEnd);
    return gradient;
  };

  const handleResetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const chartData = {
    labels: Array.from({ length: selectedTest.dataPoints.length }, (_, i) => i + 1),
    datasets: [{
      label: "Pressure",
      data: selectedTest.dataPoints,
      borderColor: function(context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        return chartArea ? createGradient(
          ctx,
          chartArea,
          "rgba(75, 192, 192, 0.8)",
          "rgba(75, 192, 192, 1)"
        ) : "rgba(75, 192, 192, 1)";
      },
      backgroundColor: function(context) {
        const chart = context.chart;
        const { ctx, chartArea } = chart;
        return chartArea ? createGradient(
          ctx,
          chartArea,
          "rgba(75, 192, 192, 0.1)",
          "rgba(75, 192, 192, 0.5)"
        ) : "rgba(75, 192, 192, 0.4)";
      },
      pointRadius: pointRadiusArray,
      pointHoverRadius: 8,
      pointBackgroundColor: "rgba(75, 192, 192, 1)",
      pointBorderColor: "#fff",
      pointBorderWidth: 1,
      tension: chartType === "smooth" ? 0.4 : chartType === "stepped" ? 0 : 0.1,
      fill: true,
      borderWidth: 2,
      stepped: chartType === "stepped",
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          title: function(context) {
            const index = context[0].dataIndex;
            return pointRadiusArray[index] > 0
              ? `Time ${secondsToHumanReadable(index)} - Note Available`
              : `Time ${secondsToHumanReadable(index)}`;
          },
          afterLabel: function(context) {
            const index = context.dataIndex;
            const dataset = context.dataset.data;
            if (index > 0) {
              const change = dataset[index] - dataset[index - 1];
              const percentChange = ((change / dataset[index - 1]) * 100).toFixed(1);
              return `Change: ${change.toFixed(2)} (${percentChange}%)`;
            }
            return "";
          },
        },
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'xy',
          modifierKey: 'shift',
        },
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.05,
            smooth: true,
            modifierKey: null
          },
          pinch: {
            enabled: true
          },
          mode: 'xy',
          drag: {
            enabled: false
          },
        },
        limits: {
          y: {min: 'original', max: 'original'},
          x: {min: 'original', max: 'original'}
        },
      },
      annotation: {
        annotations: annotations,
      },
      legend: {
        labels: {
          font: {
            size: 13,
            weight: '500'
          },
          padding: 16,
          usePointStyle: true,
        }
      }
    },
    transitions: {
      zoom: {
        animation: {
          duration: 300,
          easing: 'easeOutCubic'
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        title: {
          display: true,
          text: "Time",
          font: { size: 14, weight: "600" },
          padding: { top: 15, bottom: 5 },
          color: '#2d3748'
        },
        ticks: {
          callback: function(value) {
            return value % 2 === 0 || value === 1 ? secondsToHumanReadable(value) : "";
          },
          maxRotation: 0,
          padding: 10,
          font: {
            size: 12,
            weight: '500'
          },
          color: '#4a5568'
        },
      },
      y: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        title: {
          display: true,
          text: "Pressure",
          font: { size: 14, weight: "600" },
          padding: { top: 5, bottom: 15 },
          color: '#2d3748'
        },
        beginAtZero: true,
        ticks: {
          padding: 10,
          font: {
            size: 12,
            weight: '500'
          },
          color: '#4a5568',
          callback: function(value) {
            return value.toFixed(1);
          }
        }
      },
    },
  };

  return (
    <div className="chart-container" ref={containerRef}>
      <div className="chart-controls">
        <button 
          className="reset-zoom-button" 
          onClick={handleResetZoom}
          title="Reset Zoom"
        >
          Reset
        </button>
      </div>
      <Line 
        data={chartData} 
        options={{
          ...chartOptions,
          maintainAspectRatio: false,
          responsive: true
        }} 
        height="100%"
        ref={chartRef}
      />
      {showStats && (
        <div className="stats-panel">
          <h3>Statistical Analysis</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Mean:</span>
              <span className="stat-value">{stats.mean}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Median:</span>
              <span className="stat-value">{stats.median}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Min:</span>
              <span className="stat-value">{stats.min}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Max:</span>
              <span className="stat-value">{stats.max}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Std Dev:</span>
              <span className="stat-value">{stats.stdDev}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Trend:</span>
              <span className="stat-value">{stats.avgRateOfChange}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Anomalies:</span>
              <span className="stat-value">
                {stats.anomalies ? stats.anomalies.length : 0}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestGraph;
